/**
 * Session hooks. Thin wrappers over the orval-generated hooks that
 * unwrap the ``APIResponse`` envelope, fire toasts, and invalidate
 * related queries. Follows the mDenials-portal pattern.
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
    getGetSessionApiSessionsSessionIdGetQueryKey,
    getGetProgressApiSessionsSessionIdProgressGetQueryKey,
    useCreateSessionApiSessionsPost,
    useDeleteSessionApiSessionsSessionIdDelete,
    useGetProgressApiSessionsSessionIdProgressGet,
    useGetSessionApiSessionsSessionIdGet,
    useListSessionsApiSessionsGet,
    useSetAppealTypeApiSessionsSessionIdAppealTypePost,
} from '@/codegen/openapi/client';
import { QUERY_KEY } from '@/config/constants';
import { deferredInvalidateQueries } from './utils/deferredInvalidation';
import type { AppealTypeSelection } from '@/api/types';

export interface SetAppealTypeArgs {
    /** One primary + any additional reasons (mixed denial). */
    selections: AppealTypeSelection[];
}

/** Read the current session (documents, scenario, syntheticLocked, status). */
export function useGetSession(sessionId: string | null) {
    const queryResult = useGetSessionApiSessionsSessionIdGet(sessionId ?? '', {
        query: {
            queryKey: [QUERY_KEY.SESSION, sessionId],
            enabled: !!sessionId,
            retry: false,
        },
    });
    return {
        data: queryResult.data?.data ?? null,
        isLoading: queryResult.isPending,
        isError: queryResult.isError,
        isSuccess: queryResult.isSuccess,
        refetch: queryResult.refetch,
    };
}

/**
 * List every live session on the server. Debug-only — drives the
 * ``?debug=1`` session switcher. ``enabled`` gates the request so it
 * never fires in the normal (non-debug) UI. Refetches periodically so
 * freshly created sessions show up without a manual reload.
 */
export function useListSessions(enabled: boolean) {
    const queryResult = useListSessionsApiSessionsGet({
        query: {
            queryKey: [QUERY_KEY.SESSIONS],
            enabled,
            retry: false,
            refetchInterval: enabled ? 5000 : false,
        },
    });
    return {
        data: queryResult.data?.data ?? [],
        isLoading: queryResult.isPending,
        refetch: queryResult.refetch,
    };
}

/** Poll the activity log while the agent is running. */
export function useGetProgress(sessionId: string | null, opts?: { polling?: boolean }) {
    const queryResult = useGetProgressApiSessionsSessionIdProgressGet(sessionId ?? '', {
        query: {
            queryKey: [QUERY_KEY.PROGRESS, sessionId],
            enabled: !!sessionId,
            retry: false,
            refetchInterval: opts?.polling ? 1500 : false,
        },
    });
    return {
        data: queryResult.data?.data ?? [],
        isLoading: queryResult.isPending,
    };
}

/** Create a new session (lazy bootstrap). */
export function useCreateSession() {
    const { mutateAsync, isPending } = useCreateSessionApiSessionsPost();
    const create = useCallback(async () => {
        const r = await mutateAsync();
        if (!r.data) throw new Error(r.error?.toString() ?? 'Failed to create session');
        return r.data;
    }, [mutateAsync]);
    return useMemo(() => ({ createSession: create, isPending }), [create, isPending]);
}

/** Delete a session. */
export function useDeleteSession() {
    const { mutateAsync, isPending } = useDeleteSessionApiSessionsSessionIdDelete();
    const remove = useCallback(
        (sessionId: string) => mutateAsync({ sessionId }),
        [mutateAsync],
    );
    return useMemo(() => ({ deleteSession: remove, isPending }), [remove, isPending]);
}

/** Set the appeal type (locks the session into synthetic mode). */
export function useSetAppealType(sessionId: string | null) {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useSetAppealTypeApiSessionsSessionIdAppealTypePost();
    const setAppealType = useCallback(
        async (data: SetAppealTypeArgs) => {
            if (!sessionId) throw new Error('No session');
            const r = await mutateAsync({ sessionId, data });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Set appeal type failed');
            deferredInvalidateQueries(queryClient, [QUERY_KEY.SESSION, sessionId]);
            return r.data;
        },
        [mutateAsync, queryClient, sessionId],
    );
    return useMemo(() => ({ setAppealType, isPending }), [setAppealType, isPending]);
}

/** Query-key helpers — re-exported so callers can imperatively invalidate. */
export const sessionQueryKey = (sessionId: string) =>
    getGetSessionApiSessionsSessionIdGetQueryKey(sessionId);
export const progressQueryKey = (sessionId: string) =>
    getGetProgressApiSessionsSessionIdProgressGetQueryKey(sessionId);
